import { Box, Chip, LinearProgress, Stack, Typography } from "@mui/material";
import type { BatchTrackingPhase, TrackedBatch } from "../batchGeneration";

const TERMINAL_PHASES = new Set<BatchTrackingPhase>(["completed", "partial_failure", "failed", "cancelled", "blocked"]);

export const isActiveProductBatch = (batch?: TrackedBatch): boolean => Boolean(batch && !TERMINAL_PHASES.has(batch.phase));

const statusDetails = (phase: BatchTrackingPhase) => {
    switch (phase) {
        case "completed":
            return { label: "Completed", color: "success" as const };
        case "partial_failure":
            return { label: "Completed with issues", color: "warning" as const };
        case "failed":
        case "blocked":
            return { label: phase === "blocked" ? "Blocked" : "Failed", color: "error" as const };
        case "cancelled":
            return { label: "Cancelled", color: "default" as const };
        case "cancelling":
            return { label: "Cancelling", color: "warning" as const };
        case "checking":
            return { label: "Checking context", color: "primary" as const };
        case "running":
            return { label: "In progress", color: "primary" as const };
        default:
            return { label: "Queued", color: "primary" as const };
    }
};

const summaryFor = (batch?: TrackedBatch, checking?: boolean): string => {
    if (checking) return "Validating Context Mesh requirements…";
    if (!batch) return "";
    const { completed, failed, cancelled, total } = batch.counts;
    if (batch.phase === "completed" && total === 0) return "All Work Products are already current.";
    if (batch.phase === "partial_failure") {
        const cancelledText = cancelled > 0 ? ` · ${cancelled} cancelled` : "";
        return `${completed} generated · ${failed} failed${cancelledText}`;
    }
    if (batch.phase === "failed") return batch.message;
    if (batch.phase === "cancelled") return `${completed} generated · ${cancelled} cancelled`;
    if (total > 0) return `${completed} of ${total} generated`;
    return batch.message;
};

interface ProductBatchGenerationStatusProps {
    productName: string;
    batch?: TrackedBatch;
    checking?: boolean;
    compact?: boolean;
    onOpenDetails?: () => void;
}

const ProductBatchGenerationStatus = ({ productName, batch, checking = false, compact = false, onOpenDetails }: ProductBatchGenerationStatusProps) => {
    if (!batch && !checking) return null;

    const phase: BatchTrackingPhase = checking ? "checking" : batch!.phase;
    const status = statusDetails(phase);
    const active = checking || isActiveProductBatch(batch);
    const completed = phase === "completed";
    const progressPresentation = active || completed;
    const accentColor = phase === "cancelling" ? "warning.main" : completed ? "success.main" : "primary.main";
    const counts = batch?.counts;
    const resolved = counts ? counts.completed + counts.failed + counts.cancelled : 0;
    const progress = counts && counts.total > 0 ? Math.round((resolved / counts.total) * 100) : phase === "completed" ? 100 : null;

    return (
        <Box
            component={onOpenDetails ? "button" : "div"}
            type={onOpenDetails ? "button" : undefined}
            aria-label={onOpenDetails ? `View batch generation details for ${productName}` : undefined}
            onClick={event => {
                if (!onOpenDetails) return;
                event.stopPropagation();
                onOpenDetails();
            }}
            onKeyDown={event => {
                if (onOpenDetails) event.stopPropagation();
            }}
            data-testid={`product-batch-status-${productName}`}
            sx={{
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                mx: -1,
                my: -0.5,
                p: 1,
                pr: 3,
                border: 0,
                background: "transparent",
                color: "inherit",
                font: "inherit",
                textAlign: "left",
                cursor: onOpenDetails ? "pointer" : "default",
                borderRadius: 1,
                "&:focus-visible": onOpenDetails ? { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 3 } : undefined
            }}
        >
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap={phase === "checking" ? "wrap" : "nowrap"} useFlexGap>
                {progressPresentation ? (
                    <>
                        <Box
                            component="span"
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                bgcolor: accentColor,
                                flex: "0 0 auto"
                            }}
                        />
                        <Typography
                            component="span"
                            sx={{
                                flex: phase === "checking" ? "0 0 auto" : 1,
                                fontSize: compact ? 9.5 : 10.5,
                                fontWeight: 800,
                                color: accentColor,
                                textTransform: "uppercase",
                                letterSpacing: "0.07em"
                            }}
                        >
                            {phase === "cancelling"
                                ? "Cancelling"
                                : completed
                                  ? "Completed"
                                  : phase === "checking"
                                    ? "Checking"
                                    : phase === "queued" || phase === "ready"
                                      ? "Queued"
                                      : "Processing"}
                        </Typography>
                    </>
                ) : (
                    <Chip
                        size="small"
                        label={status.label}
                        color={status.color}
                        variant={status.color === "default" ? "outlined" : "filled"}
                        sx={{
                            height: compact ? 20 : 22,
                            flex: "0 0 auto",
                            fontSize: compact ? 9.5 : 10,
                            fontWeight: 750,
                            "& .MuiChip-label": { px: 0.8 }
                        }}
                    />
                )}
                <Typography
                    sx={{
                        minWidth: 0,
                        flex: phase === "checking" ? "1 0 100%" : undefined,
                        mt: phase === "checking" ? 0.25 : 0,
                        overflow: "hidden",
                        textOverflow: phase === "checking" ? "clip" : "ellipsis",
                        whiteSpace: phase === "checking" ? "normal" : "nowrap",
                        overflowWrap: phase === "checking" ? "anywhere" : undefined,
                        fontSize: compact ? 10.5 : 11,
                        fontWeight: progressPresentation ? 700 : 600,
                        fontFamily: progressPresentation ? '"JetBrains Mono", monospace' : undefined,
                        textAlign: progressPresentation && phase !== "checking" ? "right" : "left",
                        color: phase === "failed" || phase === "blocked" ? "error.main" : "text.secondary"
                    }}
                >
                    {summaryFor(batch, checking)}
                </Typography>
            </Stack>
            {progressPresentation && (
                <LinearProgress
                    aria-label={`Generate All Work Products progress for ${productName}`}
                    variant={progress === null ? "indeterminate" : "determinate"}
                    value={progress ?? undefined}
                    color={completed ? "success" : "primary"}
                    sx={{ mt: 0.75, height: compact ? 3 : 4, borderRadius: 4 }}
                />
            )}
            {progressPresentation && !compact && onOpenDetails && (
                <Typography
                    sx={{
                        mt: 1,
                        pt: 1,
                        borderTop: 1,
                        borderColor: "divider",
                        position: "relative",
                        top: 0.75,
                        fontSize: 11.5,
                        fontWeight: 650,
                        color: "primary.main"
                    }}
                >
                    View details
                </Typography>
            )}
        </Box>
    );
};

export default ProductBatchGenerationStatus;
